import {
  ActionIcon,
  Avatar,
  Group,
  Popover,
  Select,
  Text,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { PiPlus } from 'react-icons/pi';
import {
  useAddContactMember,
  useContactAssignees,
  useRemoveContactMember,
  type ContactMemberSummary,
} from '@/models/contacts';
import { UserAvatar } from '@/components/UserAvatar';

export function MembersControl(props: {
  contactId: number;
  members: ContactMemberSummary[];
  readOnly?: boolean;
}) {
  const { contactId, members, readOnly } = props;
  const remove = useRemoveContactMember();

  if (members.length === 0 && readOnly) {
    return (
      <Text size="sm" c="dimmed">
        Unassigned
      </Text>
    );
  }
  return (
    <Group gap={6} wrap="wrap">
      {members.map((m) => (
        <UserAvatar
          key={m.userId}
          name={m.name}
          email={m.email}
          picture={m.picture}
          tooltipExtra={!readOnly ? 'Click to remove' : undefined}
          onClick={
            !readOnly
              ? () => remove.mutate({ contactId, userId: m.userId })
              : undefined
          }
        />
      ))}
      {!readOnly && <AddButton contactId={contactId} members={members} />}
    </Group>
  );
}

export function MemberAvatarGroup(props: { members: ContactMemberSummary[] }) {
  const { members } = props;
  if (members.length === 0)
    return (
      <Text size="xs" c="dimmed">
        —
      </Text>
    );
  return (
    <Avatar.Group spacing="sm">
      {members.slice(0, 3).map((m) => (
        <UserAvatar
          key={m.userId}
          name={m.name}
          email={m.email}
          picture={m.picture}
          size={26}
        />
      ))}
      {members.length > 3 && (
        <Avatar size={26} radius="xl">
          +{members.length - 3}
        </Avatar>
      )}
    </Avatar.Group>
  );
}

function AddButton(props: {
  contactId: number;
  members: ContactMemberSummary[];
}) {
  const [open, setOpen] = useState(false);
  const { data } = useContactAssignees();
  const add = useAddContactMember();
  const assignedIds = useMemo(
    () => new Set(props.members.map((m) => m.userId)),
    [props.members],
  );
  const options = (data?.users ?? [])
    .filter((u) => !assignedIds.has(u.id))
    .map((u) => ({ value: String(u.id), label: u.info?.name || u.email }));

  return (
    <Popover opened={open} onChange={setOpen} position="bottom-start" withArrow>
      <Popover.Target>
        <ActionIcon size="sm" variant="light" onClick={() => setOpen(!open)}>
          <PiPlus />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Select
          data={options}
          placeholder="Add member"
          value={null}
          onChange={(v) => {
            if (!v) return;
            add.mutate({ contactId: props.contactId, userId: Number(v) });
            setOpen(false);
          }}
          searchable
          nothingFoundMessage="No more users"
        />
      </Popover.Dropdown>
    </Popover>
  );
}
